import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogDescription,
} from '../common/Catalyst/dialog';
import { Button } from '../common/Catalyst/button';
import {
  ErrorMessage,
  Field,
  Label,
} from '../common/Catalyst/fieldset';
import { Input } from '../common/Catalyst/input';
import { UserReturn } from '../clients/rust-api-client';
import { userService } from '../clients/ApiClients';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import { apiErrorMessage, apiErrorStatus } from '../utils/apiErrors';

interface Props {
  user: UserReturn;
  onClose: () => void;
  onCorrected: (updated: UserReturn) => void;
}

/**
 * Change the address on someone else's account.
 *
 * Worth stating plainly in the UI, because the consequences are not
 * guessable from the words "correct email":
 *
 * - The new address arrives at WorkOS **unverified**, so the person still
 *   has to prove they receive mail there before they can sign in. That is
 *   the security property that makes this safe to delegate below
 *   SuperAdmin, not an inconvenience to work around.
 * - Their existing sessions end immediately. The account's identity
 *   changed, and a token minted against the old address should not
 *   outlive it.
 *
 * The backend does the real checking — same company, and never a target
 * who outranks you. This dialog only has to be honest about the outcome.
 */
const CorrectEmailDialog: React.FC<Props> = ({
  user,
  onClose,
  onCorrected,
}) => {
  const [email, setEmail] = useState<string>(user.email);
  // The field's own problem (left empty) sits under the input; the
  // server's refusal goes in an alert beside the actions.
  const [fieldError, setFieldError] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);

  const unchanged = email.trim().toLowerCase() === user.email.toLowerCase();

  const handleSave = async () => {
    setFieldError('');
    setError('');

    const trimmed = email.trim();
    if (!trimmed) {
      setFieldError('Enter the corrected address.');
      return;
    }

    setSaving(true);
    try {
      const { data } = await userService.correctUserEmail({
        id: user.id,
        userEmailCorrection: { email: trimmed },
      });
      onCorrected(data);
    } catch (err) {
      // The backend distinguishes "we refused" (400) from "WorkOS did not
      // answer" (502), and the difference is what the person should do
      // next — fix the address, or try again in a minute.
      const status = apiErrorStatus(err);
      if (status === 502) {
        setError(
          'WorkOS could not be reached, so nothing was changed. Please try again.'
        );
      } else {
        setError(apiErrorMessage(err, "Couldn't change the address."));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>Correct email address</DialogTitle>
      <DialogDescription>
        For {user.full_name}, currently {user.email}.
      </DialogDescription>
      <DialogBody>
        <Field>
          <Label>Corrected address</Label>
          <Input
            type="email"
            value={email}
            autoFocus
            onChange={(e) => setEmail(e.target.value)}
            invalid={!!fieldError}
          />
          {fieldError && <ErrorMessage>{fieldError}</ErrorMessage>}
        </Field>

        <ErrorAlert className="mt-4">{error}</ErrorAlert>

        <div className="mt-4 rounded-md bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <p>Changing this will:</p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>
              require {user.full_name} to verify the new address before they
              can sign in again
            </li>
            <li>sign them out of any session they currently have</li>
          </ul>
        </div>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          color="sky"
          onClick={handleSave}
          disabled={saving || unchanged}
        >
          {saving ? 'Saving…' : 'Correct address'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CorrectEmailDialog;
